// ═══════════════════════════════════════════════════════════════════
//  TOQUM · QA MODULE  (v2 — mobile-first, geo-tagged, scoped)
//  • QA sees ONLY their allocated suppliers/POs/exceptions/claims
//  • Daily schedule popup on login with PO/style search
//  • Factory check-in: GPS geo-tag + selfie with timestamp
//  • Inspection flow: Quality File OR Start Inspection directly
//  • Hold / Split / Merge qty management on inspection offer
//  • All forms pre-populated from Style / PO data
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  T, Page, uid, today, num, Section, Field, Grid, Btn, DataTable, TabBar, Tag, Badge,
  StatCard, Collapse, Modal, Toast, isManager, visibleSuppliers, API_URL,
} from './Shared';
import { RaiseCDRequest } from './CommercialDecision';
import DocumentHub from './DocumentHub';
import { FORMAT_REGISTRY } from './FormatSchemas';
import {
  PPMForm, MCPForm, RiskForm, InlineInspectionForm, FMEAForm, FormatRegistryTable,
} from './InspectionForms';

const PROD_URL = `${API_URL}/production_log.php`;
const postProd = (action, params={}) =>
  fetch(PROD_URL, { method:'POST', headers:{'Content-Type':'application/json'},
    body:JSON.stringify({action,...params}) }).then(r=>r.json()).catch(()=>({success:false}));

// ── Production Cross-Check Panel ─────────────────────────────────
function ProductionCrossCheck({ po, db, styleData }) {
  const [prod, setProd]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!po?.poNo) return;
    setLoading(true);
    postProd('get_summary', { po_no: po.poNo }).then(res => {
      setProd(res.success ? res : null);
      setLoading(false);
    });
  }, [po?.poNo]);

  const matrix   = po?.matrix || [];
  const totalPO  = matrix.reduce((a,m)=>a+num(m.qty),0) || num(po?.qty);
  const packByCS = {};
  (prod?.packing||[]).forEach(p=>{ packByCS[`${p.color}|${p.size}`]=num(p.qty); });
  const totalPacked = (prod?.packing||[]).reduce((a,p)=>a+num(p.qty),0);

  return (
    <Collapse title="Production & Packing Cross-Check (tamper-proof — 36hr lock)" defaultOpen>
      <div style={{ fontSize:11, color:'#8a5a00', background:'#fff8e6',
        border:'1px solid #fde68a', borderRadius:6, padding:'7px 12px', marginBottom:12 }}>
        🔒 Factory submits production via a time-locked portal link. Server timestamps every entry.
        Entries <b>lock automatically after 36 hours</b> — no backdating, no editing possible after that.
      </div>

      {loading ? <div style={{fontSize:12,color:T.textSub,padding:8}}>Loading production data…</div> : (
        <>
          {(!prod?.stages||prod.stages.length===0) ? (
            <div style={{fontSize:12,color:T.warning,padding:'8px 0'}}>
              ⚠ No production data yet for PO {po.poNo}.
              Go to Style → Supplier Links → Daily Production Entry to generate a portal link for the factory.
            </div>
          ) : (
            <div style={{ overflowX:'auto', marginBottom:12 }}>
              <table style={{ borderCollapse:'collapse', fontSize:11.5, width:'100%' }}>
                <thead><tr style={{ background:'#1f1f1f', color:'white' }}>
                  {['Stage','Total Qty','Last Entry','Locked ✓','Editable (36hr)','vs PO %'].map(h=>(
                    <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontWeight:700 }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{(prod.stages||[]).map(s=>{
                  const pct = totalPO ? Math.round(num(s.qty)/totalPO*100) : 0;
                  const col = pct>=100?T.success:pct>=80?T.warning:T.danger;
                  return (
                    <tr key={s.stage} style={{ borderBottom:`1px solid ${T.border}` }}>
                      <td style={{ padding:'7px 10px', fontWeight:700 }}>{s.stage}</td>
                      <td style={{ padding:'7px 10px', fontWeight:900, color:col }}>{num(s.qty).toLocaleString()}</td>
                      <td style={{ padding:'7px 10px', color:T.textSub, fontSize:11 }}>{s.last_date||'—'}</td>
                      <td style={{ padding:'7px 10px' }}>
                        <Tag label={`${num(s.qty_locked).toLocaleString()} pcs`} color={T.success} />
                      </td>
                      <td style={{ padding:'7px 10px' }}>
                        {num(s.qty_editable)>0
                          ? <Tag label={`${num(s.qty_editable).toLocaleString()} pcs`} color={T.warning} />
                          : <span style={{color:T.textSub,fontSize:10}}>all locked</span>}
                      </td>
                      <td style={{ padding:'7px 10px', fontWeight:800, color:col }}>{pct}%</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          )}

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))', gap:10, marginBottom:14 }}>
            {[
              ['PO Qty',              totalPO,       T.primary],
              ['Packed (declared)',   totalPacked,   totalPacked>=totalPO?T.success:T.warning],
              ['Variance',           totalPO&&totalPacked?totalPacked-totalPO:'—', totalPacked>=totalPO?T.success:T.danger],
            ].map(([l,v,c])=>(
              <div key={l} style={{ background:'white', border:`1px solid ${T.border}`,
                borderLeft:`4px solid ${c}`, borderRadius:8, padding:'10px 12px' }}>
                <div style={{ fontSize:9.5, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:3 }}>{l}</div>
                <div style={{ fontSize:20, fontWeight:900, color:T.textMain }}>
                  {typeof v==='number'?(v>0?'+':'')+v.toLocaleString():v}
                </div>
              </div>
            ))}
          </div>

          {matrix.length > 0 && (
            <div style={{ overflowX:'auto' }}>
              <div style={{ fontSize:10.5, fontWeight:800, color:T.textSub,
                textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:5 }}>
                Color / Size — PO Qty vs Factory Declared Packing
              </div>
              <table style={{ borderCollapse:'collapse', fontSize:11, width:'100%' }}>
                <thead><tr style={{ background:'#f1f5f9' }}>
                  {['Color','Country','Size','PO Qty','Packed','Variance','Status'].map(h=>(
                    <th key={h} style={{ padding:'5px 8px', textAlign:'left', fontWeight:800, borderBottom:`1px solid ${T.border}` }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{matrix.map((m,i)=>{
                  const k   = `${m.color}|${m.size}`;
                  const pk  = packByCS[k]||0;
                  const pq  = num(m.qty);
                  const diff= pk - pq;
                  const ok  = pk >= pq;
                  return (
                    <tr key={i} style={{ borderBottom:`1px solid ${T.border}`,
                      background: pk===0?`${T.danger}08`:ok?`${T.success}08`:`${T.warning}08` }}>
                      <td style={{ padding:'5px 8px', fontWeight:600 }}>{m.color||'—'}</td>
                      <td style={{ padding:'5px 8px' }}>{m.country||'—'}</td>
                      <td style={{ padding:'5px 8px' }}>{m.size||'—'}</td>
                      <td style={{ padding:'5px 8px', fontWeight:700 }}>{pq.toLocaleString()}</td>
                      <td style={{ padding:'5px 8px', fontWeight:700, color:pk===0?T.danger:ok?T.success:T.warning }}>{pk.toLocaleString()}</td>
                      <td style={{ padding:'5px 8px', fontWeight:700, color:diff>=0?T.success:T.danger }}>{diff>=0?'+':''}{diff.toLocaleString()}</td>
                      <td style={{ padding:'5px 8px' }}>
                        {pk===0 ? <Tag label="NOT DECLARED" color={T.danger} />
                          : ok  ? <Tag label="OK" color={T.success} />
                                : <Tag label={`SHORT ${Math.abs(diff)}`} color={T.warning} />}
                      </td>
                    </tr>
                  );
                })}</tbody>
              </table>
              {(prod?.editable||[]).length>0 && (
                <div style={{ marginTop:8, fontSize:11, color:T.warning, fontWeight:700 }}>
                  ⚠ {prod.editable.length} entry(ies) still within 36-hour edit window — not yet locked.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Collapse>
  );
}

const stepFormat = (name='') => {
  const n = name.toLowerCase();
  if (n.includes('pre-production') || n.includes('ppm')) return 'PPM';
  if (n.includes('control plan') || n.includes('mcp'))   return 'MCP';
  if (n.includes('risk'))                                 return 'RISK';
  if (n.includes('fmea'))                                 return 'FMEA';
  if (n.includes('inspection') || n.includes('inline') || n.includes('final') || n.includes('aql')) return 'INSP';
  return null;
};

// ── Geo-tag + selfie check-in modal ─────────────────────────────
function CheckInModal({ po, onDone, onCancel }) {
  const [step, setStep] = useState('geo'); // geo → selfie → done
  const [geo, setGeo]   = useState(null);
  const [geoErr, setGeoErr] = useState('');
  const [image, setImage]   = useState(null);
  const [loginTime]         = useState(new Date().toISOString());
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);

  useEffect(() => {
    if (step === 'geo') {
      navigator.geolocation?.getCurrentPosition(
        pos => setGeo({ lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6),
          accuracy: Math.round(pos.coords.accuracy) }),
        err => setGeoErr(`Could not get location: ${err.message}. Please enable location access.`),
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
      );
    }
    if (step === 'selfie') {
      navigator.mediaDevices?.getUserMedia({ video: { facingMode:'environment' } })
        .then(s => { setStream(s); if (videoRef.current) videoRef.current.srcObject = s; })
        .catch(() => setGeoErr('Camera not accessible. Upload a photo instead.'));
    }
    return () => { if (stream) stream.getTracks().forEach(t => t.stop()); };
  // eslint-disable-next-line
  }, [step]);

  const capture = () => {
    const v = videoRef.current; const c = canvasRef.current;
    if (!v || !c) return;
    c.width = v.videoWidth; c.height = v.videoHeight;
    const ctx = c.getContext('2d');
    ctx.drawImage(v, 0, 0);
    // Overlay timestamp + geo
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(0, c.height - 46, c.width, 46);
    ctx.fillStyle = 'white'; ctx.font = 'bold 13px sans-serif';
    ctx.fillText(`${new Date().toLocaleString()} · ${geo?.lat},${geo?.lng}`, 8, c.height - 26);
    ctx.fillText(`PO: ${po?.poNo||'—'} · ${po?.supplier||'—'}`, 8, c.height - 8);
    const data = c.toDataURL('image/jpeg', 0.85);
    setImage(data);
    if (stream) stream.getTracks().forEach(t => t.stop());
  };

  const fileUpload = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(file);
  };

  const done = () => onDone({
    geo, loginTime, image,
    checkedIn: true, checkedInAt: loginTime, factory: po?.supplier,
  });

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:3000,
      display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'white', borderRadius:16, maxWidth:480, width:'100%',
        boxShadow:'0 24px 64px rgba(0,0,0,0.4)', overflow:'hidden' }}>
        <div style={{ background:'linear-gradient(135deg,#0073ea,#005bbf)', color:'white', padding:'16px 20px' }}>
          <div style={{ fontWeight:900, fontSize:16 }}>🏭 Factory Check-In</div>
          <div style={{ fontSize:11, opacity:0.85, marginTop:2 }}>
            {po?.supplier} · PO {po?.poNo} · {po?.styleNo}
          </div>
        </div>

        <div style={{ padding:20 }}>
          {/* Step 1: Geo */}
          {step === 'geo' && (
            <div>
              <div style={{ fontWeight:700, marginBottom:12, fontSize:13 }}>Step 1 of 2 — Verify Location</div>
              {!geo && !geoErr && (
                <div style={{ textAlign:'center', padding:24, color:T.textSub, fontSize:12 }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>📍</div>
                  Detecting your location…
                </div>
              )}
              {geoErr && <div style={{ color:T.danger, fontSize:12, marginBottom:12 }}>{geoErr}</div>}
              {geo && (
                <div style={{ background:'#f0fdf4', border:`1px solid ${T.success}`, borderRadius:8,
                  padding:12, marginBottom:14, fontSize:12 }}>
                  <div style={{ fontWeight:800, color:T.success, marginBottom:4 }}>✓ Location captured</div>
                  <div>Latitude: <b>{geo.lat}</b></div>
                  <div>Longitude: <b>{geo.lng}</b></div>
                  <div>Accuracy: ±{geo.accuracy}m</div>
                  <div style={{ marginTop:4, color:T.textSub, fontSize:10.5 }}>
                    Check-in time: {new Date(loginTime).toLocaleString()}
                  </div>
                </div>
              )}
              <div style={{ display:'flex', gap:8, marginTop:8 }}>
                <Btn label={geo ? 'Next → Take Selfie' : 'Waiting…'} color={T.primary}
                  disabled={!geo} onClick={()=>setStep('selfie')} />
                <Btn label="Skip Check-in" color="#64748b" outline onClick={onCancel} />
              </div>
            </div>
          )}

          {/* Step 2: Selfie */}
          {step === 'selfie' && (
            <div>
              <div style={{ fontWeight:700, marginBottom:8, fontSize:13 }}>
                Step 2 of 2 — Photo with Factory Background
              </div>
              <div style={{ fontSize:11, color:T.textSub, marginBottom:10 }}>
                Take a selfie with the factory visible in the background. The photo will be
                geo-tagged and timestamped automatically.
              </div>
              {!image ? (
                <div>
                  <video ref={videoRef} autoPlay playsInline
                    style={{ width:'100%', borderRadius:8, background:'#000', maxHeight:240, objectFit:'cover' }} />
                  <canvas ref={canvasRef} style={{ display:'none' }} />
                  <div style={{ display:'flex', gap:8, marginTop:10, flexWrap:'wrap' }}>
                    <Btn label="📷 Capture" color={T.primary} onClick={capture} />
                    <label style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 14px',
                      background:'white', border:`1.5px solid ${T.border}`, borderRadius:7,
                      fontSize:12, fontWeight:700, cursor:'pointer', color:T.primary }}>
                      📁 Upload Photo
                      <input type="file" accept="image/*" capture="environment"
                        onChange={fileUpload} style={{ display:'none' }} />
                    </label>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ position:'relative' }}>
                    <img src={image} alt="check-in" style={{ width:'100%', borderRadius:8, maxHeight:260, objectFit:'cover' }} />
                    <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'rgba(0,0,0,0.6)',
                      color:'white', fontSize:9, padding:'4px 8px', borderRadius:'0 0 8px 8px' }}>
                      {new Date(loginTime).toLocaleString()} · {geo?.lat},{geo?.lng}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:8, marginTop:10 }}>
                    <Btn label="✓ Confirm & Check In" color={T.success} onClick={done} />
                    <Btn label="Retake" outline color={T.primary} onClick={()=>setImage(null)} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Daily Schedule Popup ─────────────────────────────────────────
function DailyPopup({ myPOs, myScheduled, db, onSelectPO, onClose }) {
  const [poQ,       setPoQ]       = useState('');
  const [styleQ,    setStyleQ]    = useState('');
  const [supplierQ, setSupplierQ] = useState('');

  const hasFilter = poQ || styleQ || supplierQ;

  const filtered = myPOs.filter(p => {
    const mPO  = !poQ       || p.poNo?.toLowerCase().includes(poQ.toLowerCase());
    const mSt  = !styleQ    || p.styleNo?.toLowerCase().includes(styleQ.toLowerCase())
                            || p.product?.toLowerCase().includes(styleQ.toLowerCase());
    const mSup = !supplierQ || p.supplier?.toLowerCase().includes(supplierQ.toLowerCase())
                            || p.brand?.toLowerCase().includes(supplierQ.toLowerCase());
    return mPO && mSt && mSup;
  });

  const POCard = ({ p, compact=false }) => (
    <div style={{ border:`1px solid ${T.border}`, borderRadius:8,
      padding:compact?'8px 10px':'10px 12px', marginBottom:6, cursor:'pointer',
      background:'white', transition:'background .12s' }}
      onMouseEnter={e=>e.currentTarget.style.background='#f5f7fb'}
      onMouseLeave={e=>e.currentTarget.style.background='white'}
      onClick={()=>{ onSelectPO(p); onClose(); }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:4 }}>
        <div>
          <div style={{ fontWeight:800, fontSize:compact?12:13, color:'#0a0e1a' }}>PO {p.poNo}</div>
          <div style={{ fontSize:compact?11:12, color:T.primary, fontWeight:700, marginTop:1 }}>
            {p.styleNo}{p.product?` · ${p.product}`:''}
          </div>
          <div style={{ fontSize:11, color:T.textSub, marginTop:1 }}>
            {p.supplier} · {p.brand} · {num(p.qty).toLocaleString()} pcs
            {p.exFactory?` · ${p.exFactory}`:''}
          </div>
        </div>
        <div style={{ display:'flex', gap:5, flexShrink:0 }}>
          <button onClick={e=>{ e.stopPropagation(); onSelectPO(p,'file'); onClose(); }}
            style={{ padding:'5px 10px', background:T.teal, color:'white', border:'none',
              borderRadius:5, fontSize:10.5, fontWeight:700, cursor:'pointer' }}>
            📁 File
          </button>
          <button onClick={e=>{ e.stopPropagation(); onSelectPO(p,'inspect'); onClose(); }}
            style={{ padding:'5px 10px', background:T.primary, color:'white', border:'none',
              borderRadius:5, fontSize:10.5, fontWeight:700, cursor:'pointer' }}>
            🔍 Inspect
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:2500,
      display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'white', borderRadius:16, maxWidth:560, width:'100%',
        maxHeight:'90vh', display:'flex', flexDirection:'column',
        boxShadow:'0 24px 64px rgba(0,0,0,0.4)' }}>

        {/* Header */}
        <div style={{ background:'linear-gradient(135deg,#12161c,#0073ea)', color:'white',
          padding:'16px 20px', borderRadius:'16px 16px 0 0',
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontWeight:900, fontSize:16 }}>Today's Inspections 👋</div>
            <div style={{ fontSize:11.5, opacity:0.8, marginTop:2 }}>
              {today()} · {myScheduled.length} scheduled · {myPOs.length} total POs
            </div>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.15)',
            border:'none', color:'white', borderRadius:6, padding:'5px 10px',
            fontSize:12, fontWeight:700, cursor:'pointer' }}>✕</button>
        </div>

        {/* Search filters */}
        <div style={{ padding:'12px 16px', borderBottom:`1px solid ${T.border}`, background:'#fafbfd' }}>
          <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase',
            letterSpacing:'0.08em', color:T.textSub, marginBottom:8 }}>
            Filter POs
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
            {[
              { label:'Style No / Name', val:styleQ, set:setStyleQ, ph:'e.g. C301, Spread…', autoFocus:true },
              { label:'PO Number',       val:poQ,    set:setPoQ,    ph:'e.g. DL/BHW/001…' },
              { label:'Supplier / Brand',val:supplierQ, set:setSupplierQ, ph:'e.g. Integrashion…' },
            ].map(f => (
              <div key={f.label}>
                <div style={{ fontSize:9, fontWeight:700, color:T.textSub,
                  textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:3 }}>
                  {f.label}
                </div>
                <input value={f.val} onChange={e=>f.set(e.target.value)}
                  placeholder={f.ph} autoFocus={f.autoFocus||false}
                  style={{ width:'100%', padding:'7px 9px', border:`1.5px solid ${T.border}`,
                    borderRadius:6, fontSize:12, outline:'none', boxSizing:'border-box',
                    fontFamily:"'Inter',sans-serif" }}
                  onFocus={e=>e.target.style.borderColor='#4f6ef7'}
                  onBlur={e=>e.target.style.borderColor=T.border} />
              </div>
            ))}
          </div>
          {hasFilter && (
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:6, alignItems:'center' }}>
              <span style={{ fontSize:11, color:T.textSub }}>
                <b>{filtered.length}</b> PO{filtered.length!==1?'s':''} found
              </span>
              <button onClick={()=>{ setPoQ(''); setStyleQ(''); setSupplierQ(''); }}
                style={{ fontSize:11, color:T.danger, background:'none', border:'none',
                  cursor:'pointer', fontWeight:700 }}>✕ Clear</button>
            </div>
          )}
        </div>

        {/* Results */}
        <div style={{ flex:1, overflowY:'auto', padding:'10px 12px' }}>

          {/* Today scheduled — shown when no filters active */}
          {!hasFilter && myScheduled.length > 0 && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase',
                color:T.textSub, letterSpacing:'0.06em', marginBottom:6,
                display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ width:6, height:6, borderRadius:'50%', background:T.primary, display:'inline-block' }}/>
                Scheduled Today
              </div>
              {myScheduled.map(p => (
                <div key={p.poNo} style={{ background:`${T.primary}0d`, border:`1.5px solid ${T.primary}33`,
                  borderRadius:8, padding:'10px 12px', marginBottom:6, cursor:'pointer' }}
                  onClick={()=>{ onSelectPO(p); onClose(); }}>
                  <div style={{ fontWeight:800, fontSize:13 }}>PO {p.poNo}</div>
                  <div style={{ fontSize:12, color:T.primary, fontWeight:700 }}>{p.styleNo}{p.product?` · ${p.product}`:''}</div>
                  <div style={{ fontSize:11, color:T.textSub, marginTop:2 }}>
                    {p.supplier} · {p.brand} · {num(p.qty).toLocaleString()} pcs
                  </div>
                  <div style={{ marginTop:7, display:'flex', gap:6 }}>
                    <button onClick={e=>{ e.stopPropagation(); onSelectPO(p,'file'); onClose(); }}
                      style={{ padding:'5px 12px', background:T.teal, color:'white', border:'none',
                        borderRadius:5, fontSize:11, fontWeight:700, cursor:'pointer' }}>
                      📁 Quality File
                    </button>
                    <button onClick={e=>{ e.stopPropagation(); onSelectPO(p,'inspect'); onClose(); }}
                      style={{ padding:'5px 12px', background:T.primary, color:'white', border:'none',
                        borderRadius:5, fontSize:11, fontWeight:700, cursor:'pointer' }}>
                      🔍 Start Inspection
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Filtered results */}
          {hasFilter && (
            <div>
              <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase',
                color:T.textSub, letterSpacing:'0.06em', marginBottom:6,
                display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ width:6, height:6, borderRadius:'50%', background:T.success, display:'inline-block' }}/>
                {filtered.length > 0 ? `${filtered.length} Result${filtered.length!==1?'s':''}` : 'No Matches'}
              </div>
              {filtered.length === 0 ? (
                <div style={{ textAlign:'center', padding:'20px 16px', background:'#f8f9fc',
                  borderRadius:8, color:T.textSub, fontSize:12 }}>
                  No POs match the current filters.
                  <div style={{ marginTop:4, fontSize:11 }}>Try adjusting or clearing a filter.</div>
                </div>
              ) : filtered.map(p => <POCard key={p.poNo} p={p} />)}
            </div>
          )}

          {/* All POs shown when no filter and nothing scheduled */}
          {!hasFilter && myScheduled.length === 0 && (
            <div>
              <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase',
                color:T.textSub, letterSpacing:'0.06em', marginBottom:6 }}>
                All Your POs — use filters above to search
              </div>
              {myPOs.slice(0,8).map(p => <POCard key={p.poNo} p={p} compact />)}
              {myPOs.length > 8 && (
                <div style={{ textAlign:'center', fontSize:11, color:T.textSub, padding:8 }}>
                  + {myPOs.length - 8} more — type a style no or PO to filter
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'10px 16px', borderTop:`1px solid ${T.border}`,
          background:'#fafbfd', display:'flex', justifyContent:'flex-end' }}>
          <Btn small outline label="Close" color={T.primary} onClick={onClose} />
        </div>
      </div>
    </div>
  );
}

// ── Qty Split / Hold / Merge dialog ─────────────────────────────
function QtyManager({ po, existingSplits=[], onSave, onClose }) {
  const totalQty = num(po.qty);
  const [splits, setSplits] = useState(existingSplits.length ? existingSplits : [
    { id: uid(), label:'Shipment 1', qty: totalQty, status:'Pending Inspection',
      colors:[], countries:[], notes:'' }
  ]);
  const [merge, setMerge] = useState({ ids:[], finalLabel:'Merged Final' });
  const allocated = splits.reduce((a,s) => a + num(s.qty), 0);
  const unallocated = totalQty - allocated;

  const addSplit = () => setSplits(s => [...s, {
    id: uid(), label:`Shipment ${s.length+1}`, qty: unallocated > 0 ? unallocated : 0,
    status:'Pending Inspection', colors:[], countries:[], notes:''
  }]);

  const update = (id, k, v) => setSplits(s => s.map(x => x.id===id ? {...x,[k]:v} : x));

  const statusColor = s =>
    s==='On Hold' ? T.warning : s==='Cancelled' ? T.danger :
    s==='Passed' ? T.success : s==='Merged' ? T.purple : T.primary;

  return (
    <Modal title={`Qty Management — PO ${po.poNo} · Total ${totalQty.toLocaleString()} pcs`}
      onClose={onClose} width={700}
      actions={<><Btn label="Save" color={T.success} onClick={()=>onSave(splits)} /><Btn label="Cancel" color="#64748b" onClick={onClose} /></>}>
      <div style={{ fontSize:11.5, background:`${unallocated<0?T.danger:T.primary}0d`,
        border:`1px solid ${unallocated<0?T.danger:T.border}`, borderRadius:6,
        padding:'8px 12px', marginBottom:14, fontWeight:700 }}>
        Allocated: {allocated.toLocaleString()} / {totalQty.toLocaleString()} pcs
        {unallocated > 0 && <span style={{ color:T.warning }}> · {unallocated.toLocaleString()} unallocated</span>}
        {unallocated < 0 && <span style={{ color:T.danger }}> · OVER by {Math.abs(unallocated).toLocaleString()} — reduce split qty</span>}
      </div>

      {splits.map((s, i) => (
        <div key={s.id} style={{ border:`1px solid ${statusColor(s.status)}44`,
          borderLeft:`4px solid ${statusColor(s.status)}`, borderRadius:8,
          padding:12, marginBottom:10, background:'white' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
            <input value={s.label} onChange={e=>update(s.id,'label',e.target.value)}
              style={{ fontWeight:800, fontSize:13, border:'none', background:'transparent', outline:'none' }} />
            <div style={{ display:'flex', gap:6, alignItems:'center' }}>
              <select value={s.status} onChange={e=>update(s.id,'status',e.target.value)}
                style={{ padding:'4px 8px', borderRadius:6, border:`1px solid ${T.border}`,
                  fontSize:11, fontWeight:700, color:statusColor(s.status) }}>
                {['Pending Inspection','In Progress','On Hold','Passed','Failed','Cancelled',
                  'Awaiting Approval','Next Slot Scheduled','Merged'].map(o=><option key={o}>{o}</option>)}
              </select>
              {splits.length > 1 &&
                <button onClick={()=>setSplits(s=>s.filter(x=>x.id!==s[i].id))}
                  style={{ background:'none', border:'none', color:T.danger, cursor:'pointer', fontSize:16 }}>×</button>
              }
            </div>
          </div>
          <Grid cols={3} gap={8}>
            <div>
              <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Qty (pcs)</div>
              <input type="number" value={s.qty} onChange={e=>update(s.id,'qty',e.target.value)}
                style={{ width:'100%', padding:'6px 8px', border:`1px solid ${T.border}`,
                  borderRadius:5, fontSize:12, fontWeight:700 }} />
            </div>
            <div>
              <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Colors (comma-sep)</div>
              <input value={(s.colors||[]).join(', ')} onChange={e=>update(s.id,'colors',e.target.value.split(',').map(x=>x.trim()).filter(Boolean))}
                style={{ width:'100%', padding:'6px 8px', border:`1px solid ${T.border}`, borderRadius:5, fontSize:12 }} />
            </div>
            <div>
              <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Countries</div>
              <input value={(s.countries||[]).join(', ')} onChange={e=>update(s.id,'countries',e.target.value.split(',').map(x=>x.trim()).filter(Boolean))}
                style={{ width:'100%', padding:'6px 8px', border:`1px solid ${T.border}`, borderRadius:5, fontSize:12 }} />
            </div>
          </Grid>
          <input value={s.notes} onChange={e=>update(s.id,'notes',e.target.value)}
            placeholder="Hold reason / notes (e.g. barcode issue, color bleeding…)"
            style={{ width:'100%', marginTop:8, padding:'6px 8px', border:`1px solid ${T.border}`,
              borderRadius:5, fontSize:11, color:T.textMain, boxSizing:'border-box' }} />
          {s.status === 'On Hold' && (
            <div style={{ marginTop:6, fontSize:10.5, color:T.warning, fontWeight:700 }}>
              ⏸ On Hold — data saved in temporary state. Will become permanent on submission.
            </div>
          )}
        </div>
      ))}

      <Btn small label="+ Add Split / Shipment" outline color={T.primary} onClick={addSplit} />

      {splits.filter(s=>s.status==='Passed').length >= 2 && (
        <div style={{ marginTop:14, border:`1px solid ${T.purple}44`, borderRadius:8,
          padding:12, background:`${T.purple}08` }}>
          <div style={{ fontWeight:800, fontSize:12, color:T.purple, marginBottom:6 }}>
            🔀 Merge — Create Final Summary
          </div>
          <div style={{ fontSize:11, color:T.textSub, marginBottom:8 }}>
            Select passed shipments to merge. Final summary is only available when all selected shipments are Passed.
          </div>
          {splits.filter(s=>s.status==='Passed').map(s => (
            <label key={s.id} style={{ display:'block', fontSize:12, marginBottom:4 }}>
              <input type="checkbox" checked={merge.ids.includes(s.id)}
                onChange={e=>setMerge(m=>({ ...m, ids: e.target.checked
                  ? [...m.ids,s.id] : m.ids.filter(x=>x!==s.id) }))} />{' '}
              {s.label} — {num(s.qty).toLocaleString()} pcs
            </label>
          ))}
          {merge.ids.length >= 2 && (
            <Btn small label="Create Merged Final Record" color={T.purple}
              onClick={()=>{
                const merged = { id:uid(), label:merge.finalLabel, qty: splits.filter(s=>merge.ids.includes(s.id)).reduce((a,s)=>a+num(s.qty),0),
                  status:'Merged', mergedFrom:merge.ids, mergedOn:today() };
                setSplits(s=>[...s.filter(x=>!merge.ids.includes(x.id)).map(x=>merge.ids.includes(x.id)?{...x,status:'Merged'}:x), merged]);
                setMerge({ids:[],finalLabel:'Merged Final'});
              }} />
          )}
        </div>
      )}
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════
export default function QAModule({ db, setDb, saveToDB, currentUser }) {
  const TABS = ['MY SUPPLIERS','PO QUALITY FILE','DOCUMENT HUB','INSPECTION REGISTER',
                'INSPECTION OFFERS','EXCEPTIONS','FORMAT REGISTER'];
  const [tab, setTab]           = useState('MY SUPPLIERS');
  const [activePO, setActivePO] = useState(null);
  const [activeForm, setActiveForm] = useState(null);
  const [toast, setToast]       = useState(null);
  const [showPopup, setShowPopup] = useState(true);
  const [checkIn, setCheckIn]   = useState(null); // {po, action}
  const [cdModal, setCdModal]   = useState(null); // inspection record to raise CD for
  const [checkInData, setCheckInData] = useState({}); // keyed by poNo
  const [showQtyMgr, setShowQtyMgr] = useState(null); // po
  const notify = (msg, type='info') => { setToast({msg,type}); setTimeout(()=>setToast(null),4500); };

  // ── Scoped data — QA sees only their allocated suppliers ──────
  const mySuppliers = visibleSuppliers(currentUser, db);
  const myPOs       = (db.pos||[]).filter(p => mySuppliers.includes(p.supplier));
  const inspections = (db.inspections||[]).filter(i =>
    isManager(currentUser) ? true : mySuppliers.includes(i.supplier));
  const exceptions  = (db.exceptions||[]).filter(e =>
    isManager(currentUser) ? true : mySuppliers.includes(e.supplier));
  const records     = db.qualityRecords || [];

  // Today's scheduled = inspections in visitPlans or inspections with date=today
  const todayVisits = (db.visitPlans||[]).filter(v =>
    v.member === currentUser.name && v.date === today() && v.status === 'Scheduled');
  const myScheduled = myPOs.filter(p =>
    todayVisits.some(v => v.supplier === p.supplier) ||
    (db.inspections||[]).some(i => i.poNo===p.poNo && i.date===today() && i.status==='Scheduled')
  );

  // ── Process-flow gate ─────────────────────────────────────────
  const gateCheck = (poNo, stepName) => {
    const plan = (db.qualityPlans||[]).find(q=>q.poNo===poNo);
    if (!plan) return { ok:true, blocked:[], plan:null };
    const idx = plan.steps.findIndex(s=>s.name===stepName);
    const prior = idx > 0 ? plan.steps.slice(0,idx) : [];
    const blocked = prior.filter(s=>s.mandatory && !['Passed','Approved'].includes(s.status));
    return { ok:blocked.length===0, blocked, plan, idx };
  };

  const excApproved = (poNo, stepName) => exceptions.some(e=>
    e.poNo===poNo && e.stage===stepName && e.type==='Process Flow Exception' && e.status==='Approved');

  const raiseGateException = async (po, stepName, blocked) => {
    const entry = { id:uid(), type:'Process Flow Exception', poNo:po.poNo, supplier:po.supplier,
      stage:stepName, detail:`Prior mandatory step(s) not passed: ${blocked.map(b=>b.name).join('; ')}`,
      raisedBy:currentUser.name, raisedOn:today(), status:'Pending Approval', kpi:'Exceptions Raised' };
    setDb(p=>({...p, exceptions:[...(db.exceptions||[]), entry]}));
    await saveToDB('exception', entry);
    notify('Exception sent to the manager.','warn');
  };

  const saveRecord = async (rec, planStepName) => {
    const entry = { ...rec, enteredBy:currentUser.name, savedOn:today(),
      checkIn: checkInData[rec.poNo] || null,
      viaException: rec.viaException || false };
    setDb(p=>({...p, qualityRecords:[...(p.qualityRecords||[]).filter(r=>r.id!==entry.id), entry]}));
    await saveToDB('qualityRecord', entry);

    if (rec.format==='INSP') {
      const insp = { id:entry.id, poNo:entry.poNo, styleNo:entry.styleNo, supplier:entry.supplier,
        brand:entry.brand, stage:entry.stage, date:entry.date, qaMember:currentUser.name,
        totals:entry.totals, defects:entry.defects, header:entry.header, result:entry.result,
        status:'Completed', viaException:entry.viaException, checkIn:entry.checkIn };
      setDb(p=>({...p, inspections:[...(p.inspections||[]).filter(i=>i.id!==insp.id), insp]}));
      await saveToDB('inspection', insp);
    }

    const plan = (db.qualityPlans||[]).find(q=>q.poNo===rec.poNo);
    if (plan && planStepName) {
      const status = rec.result==='Failed' ? 'Failed'
        : rec.status==='In Progress' ? 'In Progress' : 'Passed';
      const upd = {...plan, steps: plan.steps.map(s=>s.name===planStepName
        ? {...s, status, completedOn:today(), recordId:entry.id} : s)};
      setDb(p=>({...p, qualityPlans:(p.qualityPlans||[]).map(x=>x.id===plan.id?upd:x)}));
      await saveToDB('qualityPlan', upd);
    }
    setActiveForm(null);
    notify(`${FORMAT_REGISTRY.find(f=>f.code===rec.format)?.subject||'Record'} saved.`, 'success');
  };

  // ── Handle PO selection from popup ──────────────────────────
  const handleSelectPO = (po, action) => {
    setActivePO(po);
    if (action === 'inspect') {
      // Trigger check-in first
      const alreadyCheckedIn = checkInData[po.poNo];
      if (!alreadyCheckedIn) {
        setCheckIn({ po, action });
      } else {
        const insp = (db.qualityPlans||[]).find(q=>q.poNo===po.poNo)?.steps.find(s=>stepFormat(s.name)==='INSP');
        if (insp) openStep(po, insp);
      }
    }
    setTab('PO QUALITY FILE');
  };

  const openStep = (po, step) => {
    const code = stepFormat(step.name);
    if (!code) return alert(`No format mapped to "${step.name}".`);

    // ── GEO-TAG GATE — QA must check in before filling any form ──
    const alreadyCheckedIn = checkInData[po.poNo];
    if (!alreadyCheckedIn) {
      setCheckIn({ po, action:'inspect', pendingStep: step });
      return; // will re-open step after check-in completes
    }

    const gate = gateCheck(po.poNo, step.name);
    const approved = excApproved(po.poNo, step.name);
    const poRecords = records.filter(r=>r.poNo===po.poNo);
    const existing = poRecords.find(r=>r.format===code && r.stepName===step.name);
    if (!gate.ok && !approved) {
      setActiveForm({ code, step:step.name, gateBlocked:true, blocked:gate.blocked,
        gateInfo:`Previous mandatory step(s) not Passed/Approved: ${gate.blocked.map(b=>`${b.seq}. ${b.name}`).join(' · ')}` });
      return;
    }
    setActiveForm({ code, step:step.name, existing, viaException:!gate.ok&&approved });
  };

  // ═══ MY SUPPLIERS ════════════════════════════════════════════
  const MySuppliers = () => (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:12, marginBottom:18 }}>
        <StatCard label="My Suppliers" value={mySuppliers.length} color={T.primary} />
        <StatCard label="Open POs" value={myPOs.length} color={T.teal} />
        <StatCard label="My Inspections" value={inspections.filter(i=>i.qaMember===currentUser.name).length} color={T.purple} />
        <StatCard label="Today's Schedule" value={myScheduled.length} color={T.warning} />
      </div>
      {mySuppliers.map(sup => {
        const supPOs = myPOs.filter(p=>p.supplier===sup);
        const s = (db.suppliers||[]).find(x=>x.name===sup);
        return (
          <Collapse key={sup} defaultOpen title={`🏭 ${sup}`}
            badge={`${supPOs.length} PO(s)`}>
            {s && <div style={{ fontSize:11, color:T.textSub, marginBottom:8 }}>
              {s.factoryAddress?.city||s.address?.city||'—'} · {s.totalLines||0} lines · AQL {s.qualitySetup?.aqlStandard||'—'}
            </div>}
            <div style={{ overflowX:'auto' }}>
              <DataTable columns={['PO No','Style','Brand','PO Date','Qty','Progress','Actions']}
                rows={supPOs}
                renderCell={(r,c) => {
                  const plan = (db.qualityPlans||[]).find(q=>q.poNo===r.poNo);
                  const done = plan ? plan.steps.filter(x=>['Passed','Approved'].includes(x.status)).length : 0;
                  const tot  = plan ? plan.steps.length : 0;
                  const splits = r.qtySplits || [];
                  return ({
                    'PO No':<b>{r.poNo}</b>, Style:r.styleNo, Brand:r.brand, 'PO Date':r.poDate,
                    Qty: <span>{num(r.qty).toLocaleString()}
                      {splits.length>1 && <Tag label={`${splits.length} splits`} color={T.purple} />}
                    </span>,
                    'Progress': tot
                      ? <span style={{ display:'flex', alignItems:'center', gap:5 }}>
                          <span style={{ width:50, height:5, background:'#f1f5f9', borderRadius:3, display:'inline-block' }}>
                            <span style={{ display:'block', height:5, width:`${done/tot*100}%`, borderRadius:3,
                              background:done===tot?T.success:T.primary }}/></span>
                          {done}/{tot}
                        </span>
                      : <Tag label="No plan" color={T.warning} />,
                    'Actions': <span style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                      <Btn small label="Quality File" onClick={()=>{ setActivePO(r); setTab('PO QUALITY FILE'); }} />
                      <Btn small outline color={T.purple} label="Qty ⟷" onClick={()=>setShowQtyMgr(r)} />
                    </span>
                  })[c];
                }}
                emptyMsg="No POs for this supplier." />
            </div>
          </Collapse>
        );
      })}
      {mySuppliers.length===0 && <Section title="My Suppliers">
        <span style={{ fontSize:12, color:T.textSub }}>
          No suppliers allocated to you yet. Ask your manager to assign them in Admin → Allocation.
        </span></Section>}
    </div>
  );

  // ═══ PO QUALITY FILE ════════════════════════════════════════
  const QualityFile = () => {
    if (!activePO) return (
      <Section title="PO Quality File">
        <Field label="Select a PO" value="" onChange={e=>{
          const p=myPOs.find(x=>x.poNo===e.target.value); if(p) setActivePO(p);}}
          options={myPOs.map(p=>p.poNo)} />
      </Section>
    );

    const po = activePO;
    const style = (db.styles||[]).find(s=>s.styleNo===po.styleNo) || {};
    const plan  = (db.qualityPlans||[]).find(q=>q.poNo===po.poNo);
    const poRecords = records.filter(r=>r.poNo===po.poNo);
    const ci = checkInData[po.poNo];
    const splits = po.qtySplits || [];

    // ── GEO-TAG REQUIRED BANNER ─────────────────────────────────
    if (!ci) return (
      <div style={{ padding:24 }}>
        <div style={{ background:'linear-gradient(135deg,#fff8e6,#fff3d6)',
          border:'2px solid #fde68a', borderRadius:14, padding:'28px 22px',
          textAlign:'center', maxWidth:480, margin:'0 auto' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>📍</div>
          <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",
            fontSize:17, fontWeight:800, color:'#7c5c00', marginBottom:8 }}>
            Location Verification Required
          </div>
          <div style={{ fontSize:13, color:'#a07830', lineHeight:1.65, marginBottom:20 }}>
            You must geo-tag your location at the factory before accessing any inspection form.
            This verifies you are physically present at <b>{po.supplier}</b>.
          </div>
          <Btn label="📍 Check In & Verify Location" color={T.primary}
            onClick={()=>setCheckIn({ po, action:'checkin' })}/>
          <div style={{ fontSize:11, color:'#b89050', marginTop:10 }}>
            GPS location + selfie required · Takes less than a minute
          </div>
        </div>
      </div>
    );

    if (activeForm) {
      const common = { po, currentUser, existing:activeForm.existing,
        onCancel:()=>setActiveForm(null),
        onSave:rec=>saveRecord({...rec,viaException:activeForm.viaException},activeForm.step),
        // Pass style/spec data for pre-population
        styleData: style,
        specData: (db.specSheets||[]).find(s=>s.styleNo===po.styleNo),
        checkInData: ci,
      };
      switch (activeForm.code) {
        case 'PPM':  return <PPMForm  {...common} />;
        case 'MCP':  return <MCPForm  {...common} />;
        case 'RISK': return <RiskForm {...common} />;
        case 'FMEA': return <FMEAForm {...common} />;
        case 'INSP': return <InlineInspectionForm {...common}
          defectCodes={db.defectCodes||[]}
          gateBlocked={activeForm.gateBlocked} gateInfo={activeForm.gateInfo}
          checkInData={{ ...(ci||{}), productionLogs: db.productionLogs||[] }}
          onRaiseCD={(rec)=>{ setCdModal(rec); setActiveForm(null); }}
          onRaiseException={activeForm.gateBlocked
            ? ()=>raiseGateException(po,activeForm.step,activeForm.blocked):null} />;
        default: return null;
      }
    }

    return (
      <div>
        {/* Check-in banner */}
        {ci ? (
          <div style={{ background:'#f0fdf4', border:`1px solid ${T.success}`, borderRadius:8,
            padding:'10px 14px', marginBottom:12, fontSize:12, display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
            <span>✓ <b>Checked in</b> at {new Date(ci.loginTime).toLocaleTimeString()} · 📍 {ci.geo?.lat},{ci.geo?.lng}</span>
            {ci.image && <img src={ci.image} alt="check-in" style={{ height:36, borderRadius:4, border:'1px solid #ccc' }} />}
          </div>
        ) : (
          <div style={{ background:`${T.warning}18`, border:`1px solid ${T.warning}`, borderRadius:8,
            padding:'10px 14px', marginBottom:12, fontSize:12, display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            <span>⚠ You have not checked in at this factory yet.</span>
            <Btn small label="Check In Now" color={T.warning}
              onClick={()=>setCheckIn({po, action:'file'})} />
          </div>
        )}

        <Section title={`Quality File — PO ${po.poNo} · ${po.styleNo} · ${po.supplier}`}
          actions={<span style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            <Btn small outline color={T.teal} label="📁 Docs" onClick={()=>setTab('DOCUMENT HUB')} />
            <Btn small outline color={T.purple} label="Qty ⟷" onClick={()=>setShowQtyMgr(po)} />
            <Btn small color="#64748b" label="← Back" onClick={()=>setActivePO(null)} />
          </span>}>

          {/* PO + Style info panel */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:10, marginBottom:14 }}>
            <StatCard label="Brand" value={po.brand} color={T.primary} />
            <StatCard label="PO Qty" value={num(po.qty).toLocaleString()} color={T.teal} />
            <StatCard label="Ex-Factory" value={po.exFactory||'—'} color={T.warning} />
            <StatCard label="Style" value={po.styleNo} color={T.purple} />
            <StatCard label="Plan Steps" value={plan?plan.steps.length:0}
              sub={plan?`${plan.steps.filter(s=>['Passed','Approved'].includes(s.status)).length} done`:'no plan'} color={T.success} />
          </div>

          {/* Production Cross-Check — reads from tamper-proof 36hr-locked log */}
          <ProductionCrossCheck po={po} db={db} styleData={style} />

          {/* Splits summary */}
          {splits.length > 1 && (
            <Collapse title={`Qty Splits (${splits.length})`}>
              {splits.map(s=>(
                <div key={s.id} style={{ display:'flex', gap:10, alignItems:'center',
                  fontSize:11.5, padding:'5px 0', borderBottom:`1px solid ${T.border}` }}>
                  <span style={{ flex:1, fontWeight:700 }}>{s.label}</span>
                  <span>{num(s.qty).toLocaleString()} pcs</span>
                  <Badge status={s.status} />
                </div>
              ))}
              {splits.filter(s=>s.status==='Merged').length > 0 && (
                <div style={{ marginTop:8, padding:8, background:`${T.purple}0d`,
                  border:`1px solid ${T.purple}44`, borderRadius:6, fontSize:11.5 }}>
                  🔀 <b>Merged shipments detected.</b> Final summary available below.
                </div>
              )}
            </Collapse>
          )}
        </Section>

        {/* Quality plan steps */}
        {!plan && <div style={{ fontSize:12, color:T.warning, padding:'8px 0' }}>
          No quality plan for this PO. Define the brand's process in Admin → Quality Process.
        </div>}

        {plan && (
          <Section title="Quality Plan Steps">
            <div style={{ overflowX:'auto' }}>
              <DataTable columns={['Seq','Step','Format','Scheduled','Mandatory','Status','Gate','Action']}
                rows={plan.steps.map(s=>({...s,id:s.seq}))}
                renderCell={(r,c)=>{
                  const code=stepFormat(r.name);
                  const f=FORMAT_REGISTRY.find(x=>x.code===code);
                  const gate=gateCheck(po.poNo,r.name);
                  const approved=excApproved(po.poNo,r.name);
                  const existing=poRecords.find(x=>x.format===code&&x.stepName===r.name);
                  return ({
                    Seq:<b>{r.seq}</b>, Step:r.name,
                    Format:f?<Tag label={code} color={T.teal}/>:<Tag label="—" color={T.textSub}/>,
                    Scheduled:<span style={{color:!['Passed','Approved'].includes(r.status)&&r.scheduledDate<today()?T.danger:'inherit',fontWeight:600}}>{r.scheduledDate}</span>,
                    Mandatory:r.mandatory?'Yes':'No',
                    Status:<Badge status={r.status}/>,
                    Gate:gate.ok?<Tag label="open" color={T.success}/>:approved?<Tag label="exc.approved" color={T.warning}/>:<Tag label={`blocked ${gate.blocked.length}`} color={T.danger}/>,
                    Action:<Btn small label={existing?'Open':'Fill'} onClick={()=>openStep(po,r)} />,
                  })[c];
                }} />
            </div>
          </Section>
        )}

        {poRecords.length>0 && (
          <Section title="Filed Quality Records">
            <DataTable columns={['Format','Step','Date','By','Status','Result']}
              rows={poRecords.slice().reverse()}
              renderCell={(r,c)=>({
                Format:FORMAT_REGISTRY.find(f=>f.code===r.format)?.subject||r.format,
                Step:r.stepName||'—', Date:r.date, By:r.preparedBy||r.enteredBy,
                Status:<Badge status={r.status}/>, Result:r.result?<Badge status={r.result}/>:'—' }[c])}
              actions={r=><Btn small outline label="Open"
                onClick={()=>setActiveForm({code:r.format,step:r.stepName,existing:r})} />} />
          </Section>
        )}
      </div>
    );
  };

  // ═══ INSPECTION REGISTER ════════════════════════════════════
  const Register = () => (
    <Section title="My Inspection Register">
      <DataTable columns={['Date','PO','Style','Supplier','Stage','Cri/Maj/Min','Result','Checked In']}
        rows={inspections.slice().reverse()}
        renderCell={(r,c)=>({
          Date:r.date, PO:r.poNo, Style:r.styleNo, Supplier:r.supplier, Stage:r.stage,
          'Cri/Maj/Min':r.totals?`${r.totals.critical}/${r.totals.major}/${r.totals.minor}`:'—',
          Result:<Badge status={r.result||r.status}/>,
          'Checked In':r.checkIn?.checkedIn
            ? <Tag label={`✓ ${new Date(r.checkIn.loginTime).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}`} color={T.success}/>
            : <Tag label="No" color={T.textSub}/>
        }[c])} />
    </Section>
  );

  // ═══ EXCEPTIONS (scoped) ════════════════════════════════════
  const Exceptions = () => {
    const decide = async (exc, status) => {
      if (!isManager(currentUser)) return alert('Only a QA Manager can approve exceptions.');
      const upd = {...exc, status, decidedBy:currentUser.name, decidedOn:today()};
      setDb(p=>({...p, exceptions:(db.exceptions||[]).map(x=>x.id===exc.id?upd:x)}));
      await saveToDB('exception', upd);
    };
    return (
      <div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:12, marginBottom:16 }}>
          <StatCard label="My Exceptions" value={exceptions.length} color={T.warning} />
          <StatCard label="Pending" value={exceptions.filter(e=>e.status==='Pending Approval').length} color={T.danger} />
          <StatCard label="Approved" value={exceptions.filter(e=>e.status==='Approved').length} color={T.success} />
        </div>
        <Section title={isManager(currentUser) ? 'All Exceptions' : 'Exceptions for My Suppliers'}>
          <DataTable columns={['Raised','Type','PO','Supplier','Step','Detail','By','Status']}
            rows={exceptions.slice().reverse()}
            renderCell={(r,c)=>({
              Raised:r.raisedOn, Type:r.type, PO:r.poNo, Supplier:r.supplier,
              Step:r.stage, Detail:r.detail, By:r.raisedBy, Status:<Badge status={r.status}/>}[c])}
            actions={r=>r.status==='Pending Approval'&&isManager(currentUser)&&<span style={{display:'flex',gap:5}}>
              <Btn small color={T.success} label="Approve" onClick={()=>decide(r,'Approved')} />
              <Btn small color={T.danger}  label="Reject"  onClick={()=>decide(r,'Rejected')} /></span>} />
        </Section>
      </div>
    );
  };

  // ═══ INSPECTION OFFERS ══════════════════════════════════════
  const InspectionOffers = () => {
    const [selOffer, setSelOffer] = useState(null);
    const [resp, setResp] = useState({ scheduledDate:'', notes:'', status:'Accepted' });
    const [saving, setSaving] = useState(false);

    const allOffers = (db.inspectionOffers||[])
      .filter(o => isManager(currentUser) ? true : mySuppliers.includes(o.supplier))
      .sort((a,b)=>(b.submittedAt||'').localeCompare(a.submittedAt||''));
    const pending  = allOffers.filter(o=>o.status==='Pending');
    const reviewed = allOffers.filter(o=>o.status!=='Pending');

    const respond = async () => {
      if (!selOffer) return;
      if (resp.status==='Accepted' && !resp.scheduledDate) return alert('Set a scheduled date');
      setSaving(true);
      const upd = { ...selOffer, status:resp.status, qaResponse:resp.notes,
        scheduledDate:resp.scheduledDate, respondedBy:currentUser.name,
        respondedAt:new Date().toISOString() };
      setDb(p=>({...p,inspectionOffers:(p.inspectionOffers||[]).map(o=>o.id===upd.id?upd:o)}));
      await saveToDB('inspectionOffer', upd);

      // Auto-create visitPlan so it shows in QA daily popup
      if (resp.status==='Accepted' && resp.scheduledDate) {
        const vp = {
          id: uid(), poNo: selOffer.poNo, styleNo: selOffer.styleNo,
          supplier: selOffer.supplier, brand: selOffer.brand,
          date: resp.scheduledDate, stage: selOffer.stage,
          status: 'Scheduled', qaMember: currentUser.name,
          offerId: selOffer.id, notes: resp.notes,
          createdAt: new Date().toISOString(),
        };
        setDb(p=>({...p, visitPlans:[...(p.visitPlans||[]), vp]}));
        await saveToDB('visitPlan', vp);
      }

      setSaving(false); setSelOffer(null);
      notify(`Offer ${resp.status.toLowerCase()} — ${resp.status==='Accepted'?`inspection scheduled for ${resp.scheduledDate}`:'supplier notified'}`, 'success');
    };

    const DocLink = ({file, label}) => {
      if (!file?.url) return <span style={{color:T.textSub,fontSize:11}}>Not uploaded</span>;
      const url = file.url.startsWith('http')?file.url:'https://'+file.url.replace(/^\/+/,'');
      return <a href={url} target="_blank" rel="noopener noreferrer"
        style={{display:'inline-flex',alignItems:'center',gap:4,padding:'5px 10px',
          background:T.primary,color:'white',borderRadius:5,fontSize:11,fontWeight:700,textDecoration:'none'}}>
        📄 {label}
      </a>;
    };

    return (
      <div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:12,marginBottom:18}}>
          <StatCard label="Pending" value={pending.length} color={pending.length>0?T.warning:T.success}/>
          <StatCard label="Accepted" value={allOffers.filter(o=>o.status==='Accepted').length} color={T.success}/>
          <StatCard label="Rejected" value={allOffers.filter(o=>o.status==='Rejected').length} color={T.danger}/>
          <StatCard label="Total" value={allOffers.length} color={T.primary}/>
        </div>

        {pending.map(o=>(
          <div key={o.id} style={{border:`1.5px solid ${T.warning}`,borderRadius:10,
            padding:'14px 16px',marginBottom:14,background:'#fffdf0'}}>
            <div style={{display:'flex',justifyContent:'space-between',flexWrap:'wrap',gap:8,marginBottom:10}}>
              <div>
                <div style={{fontWeight:900,fontSize:14}}>{o.stage} — PO {o.poNo} · {o.styleNo||''}</div>
                <div style={{fontSize:11.5,color:T.textSub,marginTop:2}}>
                  {o.supplier} · {o.brand} · Submitted {o.submittedAt?.split('T')[0]}
                </div>
              </div>
              <div style={{textAlign:'right'}}>
                <div style={{fontWeight:900,fontSize:18,color:T.primary}}>{num(o.offeredQty).toLocaleString()} pcs</div>
                <div style={{fontSize:11,color:T.textSub}}>Preferred: {o.preferredDate||'—'}</div>
              </div>
            </div>

            {/* Breakdown table */}
            {(o.breakdown||[]).length>0 && (
              <div style={{overflowX:'auto',marginBottom:12}}>
                <table style={{borderCollapse:'collapse',fontSize:11.5,width:'100%'}}>
                  <thead><tr style={{background:'#1f1f1f',color:'white'}}>
                    {['Color','Country','Size','PO Qty','Packed','Offered','Var'].map(h=>(
                      <th key={h} style={{padding:'6px 10px',textAlign:'left',fontWeight:700}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>{o.breakdown.map((r,i)=>{
                    const off=parseInt(r.offeredQty)||0; const diff=off-r.poQty;
                    return <tr key={i} style={{borderBottom:`1px solid ${T.border}`,
                      background:off>=r.poQty?'#f0fdf4':off>0?'#fff8e6':'white'}}>
                      <td style={{padding:'5px 10px',fontWeight:600}}>{r.color||'—'}</td>
                      <td style={{padding:'5px 10px'}}>{r.country||'—'}</td>
                      <td style={{padding:'5px 10px'}}>{r.size||'—'}</td>
                      <td style={{padding:'5px 10px',fontWeight:700}}>{num(r.poQty).toLocaleString()}</td>
                      <td style={{padding:'5px 10px',fontWeight:700,color:r.packedQty>=r.poQty?T.success:T.warning}}>{num(r.packedQty).toLocaleString()}</td>
                      <td style={{padding:'5px 10px',fontWeight:800,color:T.primary}}>{off.toLocaleString()}</td>
                      <td style={{padding:'5px 10px',fontWeight:700,color:diff>=0?T.success:T.danger}}>{diff>=0?'+':''}{diff}</td>
                    </tr>;
                  })}</tbody>
                </table>
              </div>
            )}

            {/* Documents */}
            <div style={{background:'#f8fafc',border:`1px solid ${T.border}`,borderRadius:8,padding:'10px 14px',marginBottom:10}}>
              <div style={{fontSize:10.5,fontWeight:800,textTransform:'uppercase',color:T.textSub,letterSpacing:'0.05em',marginBottom:8}}>
                Supplier Documents
              </div>
              <div style={{display:'flex',flexWrap:'wrap',gap:10}}>
                {[['PPM Meeting Form',o.ppmFile],['Packing List',o.packingListFile],
                  ['Test Reports',o.testReportFile],['Carton Chart',o.cartonChartFile],
                  ['Invoice',o.invoiceFile]].map(([label,file])=>(
                  <div key={label} style={{display:'flex',flexDirection:'column',gap:3}}>
                    <div style={{fontSize:10,color:T.textSub}}>{label}</div>
                    <DocLink file={file} label={file?'Open':'—'}/>
                  </div>
                ))}
              </div>
              {(db.supplierSubmissions||[]).filter(d=>d.supplier===o.supplier&&d.poNo===o.poNo).map(d=>(
                <div key={d.id} style={{marginTop:8,display:'flex',alignItems:'center',gap:8}}>
                  <DocLink file={d} label={d.docType}/>
                  <span style={{fontSize:10.5,color:T.textSub}}>{d.uploadedAt?.split('T')[0]}</span>
                </div>
              ))}
            </div>

            {o.notes&&<div style={{fontSize:12,color:T.textSub,marginBottom:10,
              background:'white',border:`1px solid ${T.border}`,borderRadius:6,padding:'8px 12px'}}>
              <b>Supplier notes:</b> {o.notes}</div>}

            {/* Response form */}
            <div style={{background:'white',border:`1px solid ${T.border}`,borderRadius:8,padding:'12px 14px'}}>
              <div style={{fontSize:12,fontWeight:800,marginBottom:10}}>Your Response</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:10,marginBottom:10}}>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:T.textSub,display:'block',marginBottom:3}}>Decision</label>
                  <select value={selOffer?.id===o.id?resp.status:'Accepted'}
                    onChange={e=>{setSelOffer(o);setResp(p=>({...p,status:e.target.value}));}}
                    style={{width:'100%',padding:'8px 10px',border:`1.5px solid ${T.border}`,borderRadius:7,fontSize:12}}>
                    <option value="Accepted">✓ Accept — Schedule</option>
                    <option value="Rejected">✕ Reject</option>
                    <option value="Under Review">⏳ Under Review</option>
                  </select>
                </div>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:T.textSub,display:'block',marginBottom:3}}>Inspection Date</label>
                  <input type="date" value={selOffer?.id===o.id?resp.scheduledDate:''}
                    onChange={e=>{setSelOffer(o);setResp(p=>({...p,scheduledDate:e.target.value}));}}
                    style={{width:'100%',padding:'8px 10px',border:`1.5px solid ${T.border}`,borderRadius:7,fontSize:12}}/>
                </div>
              </div>
              <textarea value={selOffer?.id===o.id?resp.notes:''}
                onChange={e=>{setSelOffer(o);setResp(p=>({...p,notes:e.target.value}));}}
                rows={2} placeholder="Message to supplier…"
                style={{width:'100%',padding:'8px 10px',border:`1.5px solid ${T.border}`,
                  borderRadius:7,fontSize:12,resize:'vertical',boxSizing:'border-box',marginBottom:10}}/>
              <Btn label={saving?'Saving…':'Send Response'}
                color={T.success} disabled={saving}
                onClick={()=>{setSelOffer(o);respond();}}/>
            </div>
          </div>
        ))}

        {reviewed.length>0&&(
          <Section title="Reviewed Offers">
            <DataTable columns={['Date','Supplier','PO','Stage','Offered','Status','Scheduled','Response']}
              rows={reviewed}
              renderCell={(r,c)=>({
                Date:r.submittedAt?.split('T')[0]||'—', Supplier:r.supplier,
                PO:<b>{r.poNo}</b>, Stage:r.stage,
                Offered:`${num(r.offeredQty).toLocaleString()} pcs`,
                Status:<Badge status={r.status}/>,
                Scheduled:r.scheduledDate||'—',
                Response:<span style={{fontSize:11,color:T.textSub}}>{r.qaResponse||'—'}</span>
              }[c])}/>
          </Section>
        )}

        {allOffers.length===0&&(
          <Section title="Inspection Offers">
            <div style={{textAlign:'center',padding:30,color:T.textSub,fontSize:12}}>
              No offers yet. Suppliers submit from their portal → Inspection Offer tab.
            </div>
          </Section>
        )}
      </div>
    );
  };


  // ═══════════════════════════════════════════════════════════
  return (
    <Page title="QUALITY ASSURANCE" subtitle="My suppliers · PO quality file · geo check-in · inspections">
      {/* Daily popup */}
      {showPopup && (
        <DailyPopup myPOs={myPOs} myScheduled={myScheduled} db={db}
          onSelectPO={handleSelectPO} onClose={()=>setShowPopup(false)} />
      )}

      {/* Check-in modal */}
      {checkIn && (
        <CheckInModal po={checkIn.po}
          onDone={(data)=>{
            const po = checkIn.po;
            const action = checkIn.action;
            const pendingStep = checkIn.pendingStep;
            setCheckInData(p=>({...p,[po.poNo]:data}));
            setCheckIn(null);
            notify(`Checked in at ${po.supplier} at ${new Date(data.loginTime).toLocaleTimeString()}`, 'success');
            if (pendingStep) {
              // Re-open the specific step that was blocked by geo-gate
              setTimeout(()=>openStep(po, pendingStep), 300);
            } else if (action==='inspect') {
              const insp = (db.qualityPlans||[]).find(q=>q.poNo===po.poNo)?.steps.find(s=>stepFormat(s.name)==='INSP');
              if (insp) setTimeout(()=>openStep(po, insp), 300);
            }
          }}
          onCancel={()=>setCheckIn(null)} />
      )}

      {/* Qty manager */}
      {showQtyMgr && (
        <QtyManager po={showQtyMgr} existingSplits={showQtyMgr.qtySplits||[]}
          onSave={async (splits)=>{
            const upd = {...showQtyMgr, qtySplits:splits};
            setDb(p=>({...p, pos:(p.pos||[]).map(x=>x.poNo===upd.poNo?upd:x)}));
            await saveToDB('po', upd);
            if (activePO?.poNo===upd.poNo) setActivePO(upd);
            setShowQtyMgr(null);
            notify('Qty splits saved.','success');
          }}
          onClose={()=>setShowQtyMgr(null)} />
      )}

      <div style={{ overflowX:'auto' }}>
        <TabBar tabs={TABS} active={tab} onChange={t=>{ setTab(t); setActiveForm(null); }} />
      </div>

      {tab==='MY SUPPLIERS'        && <MySuppliers />}
      {tab==='PO QUALITY FILE'     && <QualityFile />}
      {tab==='DOCUMENT HUB'        && (activePO
        ? <DocumentHub db={db} setDb={setDb} saveToDB={saveToDB} currentUser={currentUser}
            poNo={activePO.poNo} onClose={()=>setActivePO(null)} />
        : <Section title="Document Hub">
            <Field label="Select a PO" value="" onChange={e=>{
              const p=myPOs.find(x=>x.poNo===e.target.value); if(p) setActivePO(p);}}
              options={myPOs.map(p=>p.poNo)} /></Section>)}
      {tab==='INSPECTION REGISTER' && <Register />}
      {tab==='INSPECTION OFFERS'   && <InspectionOffers />}
      {tab==='EXCEPTIONS'          && <Exceptions />}
      {tab==='FORMAT REGISTER'     && <FormatRegistryTable records={records} />}
      <Toast msg={toast?.msg} type={toast?.type} onClose={()=>setToast(null)} />

      {/* Commercial Decision raise modal */}
      {cdModal && (
        <div style={{ position:'fixed', inset:0, zIndex:3000,
          background:'rgba(10,14,26,0.75)', display:'flex',
          alignItems:'center', justifyContent:'center',
          padding:20, backdropFilter:'blur(4px)' }}>
          <div style={{ background:'white', borderRadius:16,
            maxWidth:680, width:'100%', maxHeight:'90vh',
            overflow:'auto', boxShadow:'0 32px 80px rgba(0,0,0,0.35)' }}>
            <div style={{ padding:'16px 20px', borderBottom:`1px solid ${T.border}`,
              display:'flex', justifyContent:'space-between', alignItems:'center',
              background:'linear-gradient(90deg,#fef2f2,#fff8f8)' }}>
              <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",
                fontWeight:800, fontSize:15, color:'#dc2626' }}>
                🔴 Raise Commercial Decision Request
              </div>
              <button onClick={()=>setCdModal(null)}
                style={{ background:'none', border:'none',
                  cursor:'pointer', fontSize:20, color:'#94a3b8' }}>×</button>
            </div>
            <div style={{ padding:'18px 20px' }}>
              <RaiseCDRequest
                inspection={cdModal}
                currentUser={currentUser}
                db={db} setDb={setDb} saveToDB={saveToDB}
                onClose={()=>setCdModal(null)}
                onSuccess={()=>{ setCdModal(null); notify('CD request raised — pending manager approval','warn'); }}
              />
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
